import type { Address } from "viem";
import {
  arbitrum,
  avalanche,
  avalancheFuji,
  base,
  bsc,
  type Chain,
  coreDao,
  fraxtal,
  gnosis,
  immutableZkEvm,
  ink,
  katana,
  mainnet,
  mantle,
  monad,
  monadTestnet,
  plumeMainnet,
  polygon,
  sepolia,
  sonic,
} from "viem/chains";

import { isValidEthereumAddress } from "@/lib/utils/is-true";

import type { ChainConfig } from "./types";

// ============================================================================
// Constants and Helpers
// ============================================================================

const SWAP_FACTORY_ADDRESS = "0x8468587Af422ad440F58a57E955eCA6A970b5375";

// Common testnet addresses
const TESTNET_ADDRESSES = {
  defiWhitelist: "0x7Dda99dDDdCC91b807D2A9ae0168D3Ee0096606b" as Address,
};

// Helper to create base EVM chain config
function createBaseEvmConfig(
  chain: Chain,
  isTestnet: boolean,
  icon: string,
): Omit<ChainConfig, "features" | "prettyName"> {
  return {
    chain,
    icon,
    isEvm: true,
    isTestnet,
    validationFunc: isValidEthereumAddress,
  };
}

// ============================================================================
// Chain Configurations
// ============================================================================

/**
 * EVM chains not yet supported in wagmi/viem's chain list need to be mocked and ts-ignored.
 */
export const EVM_CHAIN_CONFIGS: Record<number, ChainConfig> = {
  // Chains with minimal features
  [arbitrum.id]: {
    ...createBaseEvmConfig(arbitrum, false, "arbitrum.svg"),
    features: {},
    prettyName: "Arbitrum",
  },
  [avalanche.id]: {
    ...createBaseEvmConfig(avalanche, false, "avalanche.svg"),
    features: {
      cross_chain: {
        // Reserved for future cross-chain implementation
      },
      defi_whitelist: {
        contractAddress: "0xAvalancheDefiWhitelistContractAddressGoesHere",
      },
      explorer: {
        txUrlBase: "https://snowscan.xyz/tx/",
      },
      rpc: {
        alchemyEndpoint: "https://avax-mainnet.g.alchemy.com/v2/${ALCHEMY_API_KEY}",
      },
      swap: {
        factoryProxyAddress: SWAP_FACTORY_ADDRESS,
      },
      fireblocks: {
        assetId: "AVAX",
      },
    },
    prettyName: "Avalanche",
  },
  [avalancheFuji.id]: {
    ...createBaseEvmConfig(avalancheFuji, true, "avalanche.svg"),
    features: {
      cross_chain: {
        // Reserved for future cross-chain implementation
      },
      defi_whitelist: {
        contractAddress: TESTNET_ADDRESSES.defiWhitelist,
      },
      explorer: {
        txUrlBase: "https://testnet.snowscan.xyz/tx/",
      },
      rpc: {
        alchemyEndpoint: "https://avax-fuji.g.alchemy.com/v2/${ALCHEMY_API_KEY}",
      },
      swap: {
        factoryProxyAddress: SWAP_FACTORY_ADDRESS,
      },
      fireblocks: {
        assetId: "AVAXTEST",
      },
    },
    prettyName: "Avalanche Fuji",
  },
  [base.id]: {
    ...createBaseEvmConfig(base, false, "base.svg"),
    features: {
      explorer: {
        txUrlBase: "https://basescan.org/tx/",
      },
      rpc: {
        alchemyEndpoint: "https://base-mainnet.g.alchemy.com/v2/${ALCHEMY_API_KEY}",
      },
    },
    prettyName: "Base",
  },
  [bsc.id]: {
    ...createBaseEvmConfig(bsc, false, "bsc.svg"),
    features: {
      swap: {
        factoryProxyAddress: SWAP_FACTORY_ADDRESS,
      },
      cross_chain: {
        // Reserved for future cross-chain implementation
      },
      defi_whitelist: {
        contractAddress: "0xBscDefiWhitelistContractAddressGoesHere",
      },
      explorer: {
        txUrlBase: "https://bscscan.com/tx/",
      },
      rpc: {
        alchemyEndpoint: "https://bnb-mainnet.g.alchemy.com/v2/${ALCHEMY_API_KEY}",
      },
      fireblocks: {
        assetId: "BNB_BSC",
      },
    },
    prettyName: "Binance Smart Chain",
  },
  [coreDao.id]: {
    ...createBaseEvmConfig(coreDao, false, "core.svg"),
    features: {},
    prettyName: "Core",
  },
  [fraxtal.id]: {
    ...createBaseEvmConfig(fraxtal, false, "fraxtal.svg"),
    features: {},
    prettyName: "Fraxtal",
  },
  [immutableZkEvm.id]: {
    ...createBaseEvmConfig(immutableZkEvm, false, "immutable.svg"),
    features: {},
    prettyName: "Immutable",
  },
  [ink.id]: {
    ...createBaseEvmConfig(ink, false, "ink.svg"),
    features: {
      explorer: {
        txUrlBase: "https://explorer.inkonchain.com/tx/",
      },
      rpc: {
        alchemyEndpoint: "https://ink-mainnet.g.alchemy.com/v2/${ALCHEMY_API_KEY}",
      },
    },
    prettyName: "Ink",
  },
  [katana.id]: {
    ...createBaseEvmConfig(katana, false, "katana.svg"),
    features: {},
    prettyName: "KATANA",
  },
  [mainnet.id]: {
    ...createBaseEvmConfig(mainnet, false, "ethereum.svg"),
    features: {
      swap: {
        factoryProxyAddress: SWAP_FACTORY_ADDRESS,
      },
      cross_chain: {
        // Reserved for future cross-chain implementation
      },
      defi_whitelist: {
        contractAddress: "0xEthereumDefiWhitelistContractAddressGoesHere",
      },
      explorer: {
        txUrlBase: "https://etherscan.io/tx/",
      },
      rpc: {
        alchemyEndpoint: "https://eth-mainnet.g.alchemy.com/v2/${ALCHEMY_API_KEY}",
      },
      fireblocks: {
        assetId: "ETH",
      },
    },
    prettyName: "Ethereum",
  },
  [mantle.id]: {
    ...createBaseEvmConfig(mantle, false, "mantle.svg"),
    features: {},
    prettyName: "Mantle",
  },
  [monad.id]: {
    ...createBaseEvmConfig(monad, false, "monad.svg"),
    features: {
      explorer: {
        txUrlBase: "https://monadscan.com/tx/",
      },
      rpc: {
        alchemyEndpoint: "https://monad-mainnet.g.alchemy.com/v2/${ALCHEMY_API_KEY}",
      },
      swap: {
        factoryProxyAddress: SWAP_FACTORY_ADDRESS,
      },
    },
    prettyName: "Monad",
  },
  [monadTestnet.id]: {
    ...createBaseEvmConfig(monadTestnet, true, "monad.svg"),
    features: {
      cross_chain: {
        // Reserved for future cross-chain implementation
      },
      defi_whitelist: {
        contractAddress: TESTNET_ADDRESSES.defiWhitelist,
      },
      explorer: {
        txUrlBase: "https://testnet.monadscan.com/tx/",
      },
      rpc: {
        alchemyEndpoint: "https://monad-testnet.g.alchemy.com/v2/${ALCHEMY_API_KEY}",
      },
      swap: {
        factoryProxyAddress: SWAP_FACTORY_ADDRESS,
      },
    },
    prettyName: "Monad Testnet",
  },
  [plumeMainnet.id]: {
    ...createBaseEvmConfig(plumeMainnet, false, "plume.svg"),
    features: {},
    prettyName: "Plume",
  },
  [polygon.id]: {
    ...createBaseEvmConfig(polygon, false, "polygon.svg"),
    features: {
      rpc: {
        alchemyEndpoint: "https://polygon-mainnet.g.alchemy.com/v2/${ALCHEMY_API_KEY}",
      },
      swap: {
        factoryProxyAddress: SWAP_FACTORY_ADDRESS,
      },
      fireblocks: {
        assetId: "MATIC_POLYGON",
      },
    },
    prettyName: "Polygon",
  },
  [sepolia.id]: {
    ...createBaseEvmConfig(sepolia, true, "ethereum.svg"),
    features: {
      swap: {
        factoryProxyAddress: SWAP_FACTORY_ADDRESS,
      },
      cross_chain: {
        // Reserved for future cross-chain implementation
      },
      defi_whitelist: {
        contractAddress: TESTNET_ADDRESSES.defiWhitelist,
      },
      explorer: {
        txUrlBase: "https://sepolia.etherscan.io/tx/",
      },
      rpc: {
        alchemyEndpoint: "https://eth-sepolia.g.alchemy.com/v2/${ALCHEMY_API_KEY}",
      },
      fireblocks: {
        assetId: "ETH_TEST5",
      },
    },
    prettyName: "Ethereum Sepolia",
  },
  [sonic.id]: {
    ...createBaseEvmConfig(sonic, false, "sonic.svg"),
    features: {
      explorer: {
        txUrlBase: "https://sonicscan.org/tx/",
      },
      rpc: {
        alchemyEndpoint: "https://sonic-mainnet.g.alchemy.com/v2/${ALCHEMY_API_KEY}",
      },
    },
    prettyName: "Sonic",
  },
  [gnosis.id]: {
    ...createBaseEvmConfig(gnosis, false, "gnosis.svg"),
    features: {
      explorer: {
        txUrlBase: "https://gnosisscan.io/tx/",
      },
      rpc: {
        alchemyEndpoint: "https://gnosis-mainnet.g.alchemy.com/v2/${ALCHEMY_API_KEY}",
      },
    },
    prettyName: "Gnosis",
  },
};

export const EVM_SUPPORTED_CHAIN_IDS = Object.keys(EVM_CHAIN_CONFIGS)
  .map(Number)
  .filter((id) => id !== gnosis.id);
